/**
 * /ambassador — Ambassador's own performance dashboard.
 *
 * Built for laziness: ONE screen, big copy-button on the link, big
 * copy-button on the coupon code, 4 stat tiles, no buried tabs.
 * The TikTok caption box at the bottom gives an ambassador a pre-written
 * post they can paste into their video description.
 *
 * Fully localised via `ambassador.dashboard.*` keys (en/es/fr/pt).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import {
  Copy,
  Sparkles,
  Eye,
  UserPlus,
  CreditCard,
  DollarSign,
  Loader2,
  Wallet,
  Heart,
  ArrowRight,
  Clock,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchAmbassadorMe } from "../lib/api";

const fmtPct = (rate) => Math.round((rate || 0) * 100);
const fmtUsd = (n) =>
  (n ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
};
// Days from now until `iso`, floored at 0. Used to drive the
// "Clearing — ready in Nd" label on the dashboard's clearing tile.
const daysUntil = (iso) => {
  if (!iso) return 0;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / 86_400_000));
  } catch {
    return 0;
  }
};

const AmbassadorDashboard = () => {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login?return_to=" + encodeURIComponent("/ambassador"), {
        replace: true,
      });
      return;
    }
    fetchAmbassadorMe()
      .then(setData)
      .catch((e) => {
        const status = e?.response?.status;
        if (status === 404) setErr("not-an-ambassador");
        else setErr(e?.response?.data?.detail || e.message || "load failed");
      });
  }, [user, loading, navigate]);

  if (err === "not-an-ambassador") {
    return (
      <div
        className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6"
        data-testid="ambassador-not-an-ambassador"
      >
        <div className="max-w-md text-center">
          <Sparkles className="h-8 w-8 text-[#9B6A6C] mx-auto mb-3" />
          <h2 className="font-serif italic text-2xl text-[#2C2123] mb-2">
            {t("ambassador.dashboard.notAAmbassadorTitle")}
          </h2>
          <p className="text-sm text-[#5C4B51] mb-4">
            <Trans
              i18nKey="ambassador.dashboard.notAAmbassadorBody"
              components={[
                <a
                  href="mailto:iyal@trubeaute.com"
                  className="text-[#9B6A6C] underline"
                />,
              ]}
            />
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#5C4B51] hover:text-[#2C2123] underline"
          >
            {t("ambassador.dashboard.backToHome")}
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#9B6A6C]" />
      </div>
    );
  }

  const copy = (val, toastMsgKey) => {
    navigator.clipboard?.writeText(val);
    toast.success(t(toastMsgKey));
  };

  const caption = t("ambassador.dashboard.captionTemplate", {
    coupon: data.coupon_code,
    url: data.referral_url,
  });
  const clearingDays = daysUntil(data.earliest_clears_at);
  const showClearing = (data.earnings_clearing_usd || 0) > 0;

  return (
    <div
      className="min-h-screen bg-[#FDFBF7]"
      data-testid="ambassador-dashboard-page"
    >
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <span className="font-serif text-xl tracking-tight text-[#2C2123]">
            TruBeauté
          </span>
          <span className="text-xs text-[#9B6A6C] bg-[#FBF4EF] border border-[#EADDD7] rounded-full px-2 py-0.5 ml-1">
            {t("ambassador.dashboard.ambassadorBadge")}
          </span>
        </div>
        <h1
          className="font-serif italic text-3xl sm:text-4xl text-[#2C2123] leading-tight mt-2"
          data-testid="ambassador-dashboard-greeting"
        >
          {t("ambassador.dashboard.greeting", {
            name: data.display_name || data.handle,
          })}{" "}
          💋
        </h1>
        <p className="text-sm text-[#8A7D82] mt-1">
          {t("ambassador.dashboard.subtitleFull", {
            ratePct: fmtPct(data.commission_rate),
          })}
        </p>

        {/* Shareable assets — the actually-important section */}
        <section className="mt-6 grid grid-cols-1 gap-3">
          <ShareCard
            label={t("ambassador.dashboard.linkLabel")}
            value={data.referral_url}
            copyLabel={t("ambassador.dashboard.linkCopy")}
            onCopy={() =>
              copy(data.referral_url, "ambassador.dashboard.linkCopiedToast")
            }
            testid="ambassador-share-link"
          />
          <ShareCard
            label={t("ambassador.dashboard.couponLabel")}
            value={data.coupon_code}
            copyLabel={t("ambassador.dashboard.linkCopy")}
            onCopy={() =>
              copy(data.coupon_code, "ambassador.dashboard.couponCopiedToast")
            }
            mono
            testid="ambassador-share-coupon"
          />
        </section>

        {/* Earnings tiles */}
        <section
          className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3"
          data-testid="ambassador-stats"
        >
          <StatTile
            icon={Eye}
            label={t("ambassador.dashboard.statClicks")}
            value={data.clicks_total}
            testid="ambassador-stat-clicks"
          />
          <StatTile
            icon={UserPlus}
            label={t("ambassador.dashboard.statSignups")}
            value={data.signups_total}
            testid="ambassador-stat-signups"
          />
          <StatTile
            icon={CreditCard}
            label={t("ambassador.dashboard.statPayingUsers")}
            value={data.paying_users_total}
            testid="ambassador-stat-paying"
          />
          <StatTile
            icon={DollarSign}
            label={t("ambassador.dashboard.statLifetimeEarned")}
            value={fmtUsd(data.earnings_total_usd)}
            testid="ambassador-stat-earned"
            highlight
          />
        </section>

        {/* Money sub-section */}
        <section
          className="mt-3 bg-white border border-[#EADDD7] rounded-2xl p-5"
          data-testid="ambassador-money"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#8A7D82] mb-1 inline-flex items-center gap-1">
                <Wallet className="h-3.5 w-3.5" />{" "}
                {t("ambassador.dashboard.owedToYou")}
              </div>
              <div
                className="font-serif italic text-3xl text-[#2C2123]"
                data-testid="ambassador-owed-amount"
              >
                {fmtUsd(data.earnings_pending_usd)}
              </div>
              <div className="text-xs text-[#8A7D82] mt-1">
                {data.last_payout_at
                  ? t("ambassador.dashboard.paidOutLineWithDate", {
                      paid: fmtUsd(data.earnings_paid_usd),
                      date: fmtDate(data.last_payout_at),
                    })
                  : t("ambassador.dashboard.paidOutLine", {
                      paid: fmtUsd(data.earnings_paid_usd),
                    })}
              </div>
              {showClearing && (
                <div
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] bg-[#FBF4EF] text-[#9B6A6C] border border-[#EADDD7] rounded-full px-2.5 py-1"
                  data-testid="ambassador-clearing-pill"
                >
                  <Clock className="h-3 w-3" />
                  <span className="font-medium tabular-nums">
                    {fmtUsd(data.earnings_clearing_usd)}
                  </span>
                  <span className="text-[#8A7D82]">·</span>
                  <span>
                    {clearingDays > 0
                      ? t("ambassador.dashboard.clearingLabel", {
                          days: clearingDays,
                        })
                      : t("ambassador.dashboard.clearingLabelToday")}
                  </span>
                </div>
              )}
            </div>
            <div className="text-xs text-[#8A7D82] max-w-xs text-right">
              <Trans
                i18nKey="ambassador.dashboard.payoutNote"
                components={[
                  <a
                    className="text-[#9B6A6C] underline"
                    href="mailto:iyal@trubeaute.com"
                  />,
                ]}
              />
            </div>
          </div>
        </section>

        {/* TikTok caption helper */}
        <section
          className="mt-6 bg-white border border-[#EADDD7] rounded-2xl p-5"
          data-testid="ambassador-caption"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-[#5C4B51] inline-flex items-center gap-1.5">
              <Heart
                className="h-3.5 w-3.5 text-[#9B6A6C]"
                fill="currentColor"
              />
              {t("ambassador.dashboard.captionTitle")}
            </div>
            <button
              onClick={() =>
                copy(caption, "ambassador.dashboard.captionCopiedToast")
              }
              className="text-xs inline-flex items-center gap-1 text-[#9B6A6C] hover:text-[#5D4050]"
              data-testid="ambassador-caption-copy"
            >
              <Copy className="h-3 w-3" /> {t("ambassador.dashboard.captionCopy")}
            </button>
          </div>
          <pre className="text-sm text-[#2C2123] whitespace-pre-wrap font-sans leading-relaxed">
            {caption}
          </pre>
        </section>

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs text-[#5C4B51] hover:text-[#2C2123]"
          >
            {t("ambassador.dashboard.backToHome")}{" "}
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </main>
    </div>
  );
};

const ShareCard = ({ label, value, copyLabel, onCopy, mono, testid }) => (
  <div
    className="bg-white border border-[#EADDD7] rounded-2xl p-4 flex items-center gap-3"
    data-testid={testid}
  >
    <div className="flex-1 min-w-0">
      <div className="text-xs uppercase tracking-wider text-[#8A7D82] mb-1">
        {label}
      </div>
      <div
        className={`truncate text-[#2C2123] ${mono ? "font-mono text-lg" : "text-sm"}`}
      >
        {value}
      </div>
    </div>
    <button
      onClick={onCopy}
      className="inline-flex items-center gap-1.5 bg-[#9B6A6C] text-white px-3 py-2 rounded-full hover:bg-[#805557] text-sm whitespace-nowrap"
    >
      <Copy className="h-3.5 w-3.5" /> {copyLabel}
    </button>
  </div>
);

const StatTile = ({ icon: Icon, label, value, testid, highlight }) => (
  <div
    className={`bg-white border ${highlight ? "border-[#9B6A6C]" : "border-[#EADDD7]"} rounded-2xl p-4`}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#8A7D82] mb-1">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <div
      className={`text-2xl font-medium ${highlight ? "text-[#9B6A6C]" : "text-[#2C2123]"}`}
    >
      {value}
    </div>
  </div>
);

export default AmbassadorDashboard;
